import { useEffect, useState } from "react";
import { KeyRound, Search, ShieldBan, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import { api, C, errorMessage, fmtChips } from "../../lib/api";
import { avatarSrc } from "../../lib/avatars";
import { Card, fmtNum, fmtWhen } from "./ui";
import { PlayerDrawer } from "./PlayerDrawer";
import { InvitePanel } from "./InvitePanel";

const flag = (cc) => (cc && cc.length === 2 ? String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)) : "");
const PAGE = 50;
const KINDS = [["real", "Real players"], ["all", "Everyone (real first)"], ["house", "House players"]];

// Registered players: search, growth-relevant columns, per-player drawer (ledger, adjust chips, block).
export const PlayersPanel = ({ isAdmin }) => {
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("real");
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get(C.ApiRoutes.ADMIN_PLAYERS, { params: { q, kind, skip: page * PAGE, limit: PAGE } }).then((r) => setData(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, kind, page]); // eslint-disable-line react-hooks/exhaustive-deps

  // Nobody — staff included — can read or set a player's password. The way back in is a one-time reset link.
  const resetLink = async (p) => {
    try {
      const { data } = await api.post(C.ApiRoutes.ADMIN_PLAYER_RESET_LINK.replace("{user_id}", p.user_id));
      await navigator.clipboard?.writeText(data.link);
      toast.success(`Reset link for ${p.display_name} copied`, { description: `Works once, expires in ${data.expires_minutes} minutes.` });
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const forget = async (p) => {
    try {
      await api.delete(`${C.ApiRoutes.ADMIN_SIGNINS}/${p.user_id}`);
      toast.success(`Old saved password wiped for ${p.display_name} — the account still works`);
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE)) : 1;
  return (
    <div data-testid="admin-players" className="flex flex-col gap-6">
      <InvitePanel onCreated={load} />
      <Card title={`Players · ${data ? fmtNum(data.total) : "…"}`}>
        <div className="flex items-center gap-3 -mt-10 mb-4 justify-end">
          <div className="flex gap-1 rounded-full bg-white/5 p-1" data-testid="players-kind-filter">
            {KINDS.map(([k, label]) => (
              <button key={k} data-testid={`players-kind-${k}`} onClick={() => { setKind(k); setPage(0); }}
                className={`rounded-full px-3 py-1 text-xs transition-colors ${kind === k ? "bg-amber-500 text-slate-900 font-semibold" : "text-slate-300 hover:text-amber-200"}`}>{label}</button>
            ))}
          </div>
          {kind === "all" && data?.real != null && <span className="text-xs text-emerald-300" data-testid="players-real-count">{fmtNum(data.real)} real</span>}
          <div className="glass rounded-full px-3 py-1.5 flex items-center gap-2 w-72">
            <Search className="w-3.5 h-3.5 text-slate-400" />
            <input data-testid="players-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder="Search name, email or id" className="bg-transparent outline-none text-sm flex-1 text-amber-50 placeholder:text-slate-500" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.2em] text-slate-400 text-left">
              <tr><th className="py-2 pr-3">Player</th><th className="px-2">Email</th><th className="px-2">Sign-in</th><th className="px-2">Country</th><th className="px-2 text-right">Chips</th><th className="px-2 text-right">Hands</th><th className="px-2 text-right">Net</th><th className="px-2">Joined</th><th className="px-2">Last seen</th><th className="px-2">Status</th></tr>
            </thead>
            <tbody>
              {(data?.players || []).map((p) => (
                <tr key={p.user_id} data-testid={`player-row-${p.user_id}`} onClick={() => setOpen(p.user_id)} className="border-t border-white/5 hover:bg-white/5 cursor-pointer">
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-2">
                      <img src={avatarSrc(p.avatar)} alt="" className="w-7 h-7 rounded-full bg-slate-800" />
                      <div><div className="font-medium">{p.display_name} <span className="font-mono text-[10px] text-amber-300/80" data-testid={`player-tag-${p.user_id}`}>{p.tag}</span></div>{p.role !== "player" && <div className="text-[9px] uppercase tracking-wider text-amber-300">{p.role}</div>}{p.house && <div className="text-[9px] uppercase tracking-wider text-sky-300" data-testid={`player-house-${p.user_id}`}>house player</div>}</div>
                    </div>
                  </td>
                  <td className="px-2 text-slate-300 text-xs">{p.email}</td>
                  <td className="px-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <span className="inline-flex items-center gap-1.5">
                      <span title="Passwords are stored as a one-way hash — nobody can read them, staff included" className="font-mono text-xs text-slate-500">••••••••</span>
                      <button data-testid={`reset-link-${p.user_id}`} onClick={() => resetLink(p)} title="Copy a one-time password-reset link to hand to this player"
                        className="rounded-full p-1 text-slate-400 hover:text-amber-200 hover:bg-white/10"><KeyRound className="w-3 h-3" /></button>
                      {p.has_stored_password && (
                        <button data-testid={`forget-password-${p.user_id}`} onClick={() => forget(p)} title="This account still has an old password saved from the invite system — click to wipe it"
                          className="rounded-full p-1 text-amber-300/80 hover:text-red-300 hover:bg-red-500/10"><X className="w-3 h-3" /></button>
                      )}
                    </span>
                  </td>
                  <td className="px-2 text-base" title={p.country || ""}>{flag(p.country)} <span className="text-xs text-slate-400">{p.country || "—"}</span></td>
                  <td className="px-2 text-right font-mono text-amber-200">{fmtChips(p.balance)}</td>
                  <td className="px-2 text-right font-mono">{fmtNum(p.hands)}</td>
                  <td className={`px-2 text-right font-mono ${p.net >= 0 ? "text-emerald-300" : "text-red-300"}`}>{p.net >= 0 ? "+" : ""}{fmtNum(p.net)}</td>
                  <td className="px-2 text-xs text-slate-400">{fmtWhen(p.created_at)}</td>
                  <td className="px-2 text-xs text-slate-400">{fmtWhen(p.last_login_at)}</td>
                  <td className="px-2">{p.blocked ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-red-300"><ShieldBan className="w-3 h-3" /> blocked</span> : <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-300"><ShieldCheck className="w-3 h-3" /> ok</span>}</td>
                </tr>
              ))}
              {data && !data.players.length && <tr><td colSpan={10} className="py-4 text-slate-500 text-xs">No players match.</td></tr>}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div className="flex items-center justify-end gap-2 mt-4 text-xs">
            <button data-testid="players-prev" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="rounded-full px-3 py-1 bg-white/5 disabled:opacity-30">Prev</button>
            <span className="text-slate-400">{page + 1} / {pages}</span>
            <button data-testid="players-next" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} className="rounded-full px-3 py-1 bg-white/5 disabled:opacity-30">Next</button>
          </div>
        )}
      </Card>
      {open && <PlayerDrawer userId={open} isAdmin={isAdmin} onClose={() => setOpen(null)} onChange={load} />}
    </div>
  );
};
